<app-page-header title="Airline Rules Definitions" subtitle="Manage airline-specific business rules and configurations">
    <div actions class="flex gap-2">
        <p-iconfield>
            <p-inputicon class="pi pi-search" />
            <input type="text" pInputText placeholder="Search" (input)="onGlobalSearch($event)" />
        </p-iconfield>
        <p-button label="Create New" icon="pi pi-plus" (click)="openCreateDialog()"></p-button>
    </div>
</app-page-header>
<div class="table-card">
    <p-table
        #dt
        [value]="airlineRules"
        [paginator]="true"
        [globalFilterFields]="globalFilterFields"
        [rows]="10"
        [rowsPerPageOptions]="[10, 25, 50]"
        [scrollable]="true"
        scrollHeight="flex"
        dataKey="id"
        [rowHover]="true"
    >
        <ng-template #header let-columns>
            <tr>
                <th *ngFor="let col of cols" [pSortableColumn]="col.field">
                    <div>
                        {{ col.header }}
                        <p-sortIcon [field]="col.field"></p-sortIcon>
                        <app-column-filter [table]="dt" type="text" [field]="col.field" display="menu"></app-column-filter>
                    </div>
                </th>
                <th></th>
            </tr>
        </ng-template>
        <ng-template pTemplate="body" let-data let-rowIndex="rowIndex">
            <tr>
                <td>{{ data.airlineCodes }}</td>
                <td>{{ data.provider.code }}</td>
                <td>{{ data.agent.name }}</td>
                <td>{{ data?.providerSupplier?.supplierName }}</td>
                <td>{{ data.ruleType }}</td>
                <td>{{ data.createdBy }}</td>
                <td>{{ data.createdAt | date: 'dd.MM.yyyy HH:mm' }}</td>
                <td>{{ data.updatedBy }}</td>
                <td>{{ data.updatedAt | date: 'dd.MM.yyyy HH:mm' }}</td>
                <td>
                    <p-toggleswitch
                        [ngModel]="data.active"
                        (ngModelChange)="onChangeToggle($event, data.id)"
                        name="active{{ rowIndex }}"
                        [ngModelOptions]="{ standalone: true }"
                    ></p-toggleswitch>
                </td>
                <td>
                    <p-button
                        severity="secondary"
                        icon="pi pi-pencil"
                        (click)="openEditDialog(data.id)"
                        variant="text"
                        class="p-button-text"
                    >
                    </p-button>
                </td>
            </tr>
        </ng-template>
    </p-table>
</div>
<p-dialog
    [header]="editMode ? 'Edit Airline Rules' : 'New Airline Rules'"
    [(visible)]="dialogVisible"
    [modal]="true"
    [draggable]="false"
    [resizable]="false"
    (onHide)="cancelAirlineRules()"
    [style]="{ width: '500px', maxHeight: '100vh' }"
    [appendTo]="'body'"
    [contentStyle]="{ overflow: 'auto', maxHeight: 'calc(100vh - 120px)' }"
>
    <form [formGroup]="airlineRulesForm" (ngSubmit)="submitAirlineRules()">
        <div class="flex flex-col mb-4">
            <div class="flex items-center gap-4">
                <label for="airlineCodes" class="text-sm font-semibold w-32"> Airline Code * </label>

                <p-multiSelect
                    [resetFilterOnHide]="true"
                    class="flex-1"
                    inputId="airlineCodes"
                    formControlName="airlineCodes"
                    [options]="airlineCodes"
                    panelStyleClass="!w-full"
                    appendTo="body"
                    display="chip"
                    [showClear]="true"
                    [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('airlineCodes') }"
                    [filter]="true"
                    [filterBy]="'name,code'"
                    placeholder="Select airline code(s)."
                    optionLabel="name"
                    optionValue="code"
                >
                    <ng-template let-opt pTemplate="item"> {{ opt.code }} - {{ opt.name }} </ng-template>

                    <!-- Seçili chip’lerde görünen -->
                    <ng-template let-opt pTemplate="selectedItem"> {{ opt.code }}-{{ opt.name }} </ng-template>
                </p-multiSelect>
            </div>
            <small
                *ngIf="airlineRulesForm.get('airlineCodes')?.invalid && airlineRulesForm.get('airlineCodes')?.touched"
                class="text-red-500 ml-[130px] mt-1"
            >
                Please select at least one airline code.
            </small>
        </div>
        <div class="flex flex-col mb-4">
            <div class="flex items-center gap-4">
                <label for="agent" class="text-sm font-semibold w-32"> Agent * </label>

                <p-select
                    class="flex-1"
                    inputId="agentId"
                    [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('agentId') }"
                    formControlName="agentId"
                    [options]="agents"
                    [disabled]="editMode"
                    panelStyleClass="!w-full"
                    appendTo="body"
                    display="chip"
                    [showClear]="true"
                    [filter]="true"
                    [resetFilterOnHide]="true"
                    placeholder="Select an agent."
                    optionLabel="name"
                    optionValue="id"
                >
                </p-select>
            </div>
            <small
                class="text-red-500 ml-[130px] mt-1"
                *ngIf="airlineRulesForm.get('agentId')?.invalid && airlineRulesForm.get('agentId')?.touched"
                >Please choose an agent.</small
            >
        </div>
        <div class="flex flex-col mb-4">
            <div class="flex items-center gap-4">
                <label for="provider" class="text-sm font-semibold w-32"> Provider * </label>
                <p-select
                    class="flex-1"
                    inputId="providerId"
                    formControlName="providerId"
                    [options]="providers"
                    [disabled]="editMode"
                    [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('providerId') }"
                    panelStyleClass="!w-full"
                    appendTo="body"
                    display="chip"
                    [showClear]="true"
                    [filter]="true"
                    [resetFilterOnHide]="true"
                    placeholder="Select a provider."
                    optionLabel="explanation"
                    (onChange)="onProviderChange($event.value)"
                    optionValue="id"
                >
                </p-select>
            </div>
            <small
                class="text-red-500 ml-[130px] mt-1"
                *ngIf="airlineRulesForm.get('providerId')?.invalid && airlineRulesForm.get('providerId')?.touched"
                >Please choose a provider.</small
            >
        </div>
        <div class="flex flex-col mb-4">
            <div class="flex items-center gap-4">
                <label for="providerSupplier" class="text-sm font-semibold w-32"> Provider Supplier * </label>
                <p-select
                    class="flex-1"
                    inputId="providerSupplierId"
                    formControlName="providerSupplierId"
                    [options]="providerSuppliers"
                    [disabled]="editMode"
                    panelStyleClass="!w-full"
                    [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('providerSupplierId') }"
                    appendTo="body"
                    display="chip"
                    [showClear]="true"
                    [filter]="true"
                    [resetFilterOnHide]="true"
                    placeholder="Select a provider supplier."
                    optionLabel="supplierName"
                    optionValue="id"
                >
                </p-select>
            </div>
            <small
                class="text-red-500 ml-[130px] mt-1"
                *ngIf="airlineRulesForm.get('providerSupplierId')?.invalid && airlineRulesForm.get('providerSupplierId')?.touched"
                >Please choose a provider supplier.</small
            >
        </div>
        <div class="flex flex-col mb-4">
            <div class="flex items-center gap-4">
                <label for="ruletype" class="text-sm font-semibold w-32"> Rule Type * </label>
                <p-select
                    class="flex-1"
                    inputId="ruleType"
                    formControlName="ruleType"
                    [ngClass]="{ 'ng-invalid ng-dirty': isInvalid('ruleType') }"
                    [options]="ruleTypeOptions"
                    panelStyleClass="!w-full"
                    appendTo="body"
                    display="chip"
                    placeholder="Select a rule type."
                    optionLabel="label"
                    optionValue="value"
                >
                </p-select>
            </div>
            <small
                class="text-red-500 ml-[130px] mt-1"
                *ngIf="airlineRulesForm.get('ruleType')?.invalid && airlineRulesForm.get('ruleType')?.touched"
                >Please choose a rule type.</small
            >
        </div>
    </form>
    <ng-template pTemplate="footer">
        <div class="flex gap-2 flex-end">
            <p-button label="Cancel" variant="outlined" (click)="cancelAirlineRules()"> </p-button>
            <p-button [label]="editMode ? 'Update' : 'Create'" (click)="submitAirlineRules()" type="submit"> </p-button>
        </div>
    </ng-template>
</p-dialog>
